import { useSyncExternalStore } from "react";

// adminActing — the acting-store source of truth for an admin working inside /merchant.
//
// This holds a request-scoped acting TARGET only. It is NOT a claim, it grants nothing on its
// own, and no StoreClaim row is ever created for it. The server independently re-authorizes
// every /api/merchant/* call through merchantAuth's explicit-storeId admin branch, so a
// tampered localStorage value buys nothing.
//
// SINGLE-PREDICATE CONTRACT (W4)
// `isAdminActingNow()` reads localStorage only, while `useMerchantStores().isAdminActing`
// additionally knows about admin status and claim-backing. They MUST NOT diverge, or a user
// gets confirmation dialogs with no banner. The invariant is: the localStorage key is present
// ONLY when acting is genuinely in effect. `useMerchantStores` owns enforcement — it clears
// the key the moment it becomes invalid (user is not an admin, or the target is claim-backed).
//
// KNOWN FIRST-PAINT WINDOW (W-D)
// `isAdminActingNow()` reads localStorage synchronously, while reconciliation settles only
// after the claims and admin queries resolve. So an admin who holds a REAL approved claim on
// the stored store can, for one paint, get a confirmation dialog with no banner. This fails
// safe (an extra confirmation, never a missing one) and ships as-is. No code change.

const ACTING_STORE_KEY = "merchant_admin_acting_store";

export type ActingStore = {
  storeId: string;
  name: string;
  slug?: string;
  logoUrl?: string;
};

let listeners: Array<() => void> = [];

const notify = () => listeners.forEach((l) => l());

// Cached snapshot: useSyncExternalStore requires a referentially-stable value between
// notifications, so we memoize the parsed object against its raw JSON string.
let cachedRaw: string | null = null;
let cachedValue: ActingStore | null = null;

export const readActingStore = (): ActingStore | null => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(ACTING_STORE_KEY);
  if (raw === cachedRaw) return cachedValue;
  cachedRaw = raw;
  if (!raw) {
    cachedValue = null;
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    cachedValue =
      parsed && typeof parsed.storeId === "string" && parsed.storeId
        ? {
            storeId: parsed.storeId,
            name: typeof parsed.name === "string" ? parsed.name : "",
            slug: typeof parsed.slug === "string" ? parsed.slug : undefined,
            logoUrl: typeof parsed.logoUrl === "string" ? parsed.logoUrl : undefined,
          }
        : null;
  } catch {
    // Malformed JSON is treated as "not acting" rather than throwing mid-render.
    cachedValue = null;
  }
  return cachedValue;
};

export const setActingStore = (store: ActingStore | null): void => {
  if (typeof window === "undefined") return;
  if (store) {
    localStorage.setItem(ACTING_STORE_KEY, JSON.stringify(store));
  } else {
    localStorage.removeItem(ACTING_STORE_KEY);
  }
  notify();
};

export const subscribeActingStore = (listener: () => void) => {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
};

export const useAdminActingStore = (): ActingStore | null =>
  useSyncExternalStore(subscribeActingStore, readActingStore, () => null);

// Non-React reader for the write gate, which runs outside any render.
export const isAdminActingNow = (): boolean => readActingStore() !== null;
